import { personaHeaderExpandedViewTooltip } from './PersonaHeader.locstring.json';
import loc, { format } from 'owa-localize';
import { observer as observerLite } from 'mobx-react-lite';
import { observer } from 'mobx-react';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { logUsage, logMountingPerformance, PerformanceDatapoint } from 'owa-analytics';
import {
    addFavoritePersona,
    lazyRemoveFavoritePersona,
    lazyHandleToggleFavoritePersonaError,
} from 'owa-mail-favorites-store';
import {
    ToggleFavoriteButton,
    FavoritesBitFlagsMasks,
    getIsBitEnabled,
    favoritesStore,
    isPersonaInFavorites,
} from 'owa-favorites';
import { SearchHeaderFirstRowContent } from 'owa-mail-search';
import { SearchHeaderFirstRowContent as SearchHeaderFirstRowContentNext } from 'owa-mail-search-next-lazy';
import { getScenarioStore, SearchScenarioId } from 'owa-search';
import { lazyGetSearchStore } from 'owa-search-next-lazy';
import { SearchScenarioId as SearchScenarioIdNext } from 'owa-search-next';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { ActionSource } from 'owa-mail-store';
import PersonaHeaderFirstRun from './../PersonaHeaderFirstRun/PersonaHeaderFirstRun';
import { default as rcStore } from 'owa-recipient-cache/lib/store/store';
import * as React from 'react';
import {
    useLivePersonaCard,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import { isEmailAddressDecoratedBrand } from 'owa-persona/lib/utils/isEmailAddressBrand';
import getFavoriteIdForPersona from 'owa-mail-favorites-store/lib/selectors/getFavoritePersona';
import { isFavoritingInProgress } from 'owa-mail-favorites-store';
import HeaderActions from './../HeaderActions/HeaderActions';
import { safePerformanceMeasure } from 'owa-search-diagnostics/lib/helpers/performanceTiming';
import { isFeatureEnabled } from 'owa-feature-flags';
import HeaderButtonData from '../../models/HeaderButtonData';

const styles = require('./PersonaHeader.scss');

export interface PersonaHeaderProps {
    personaId: string;
    personEmail: string;
    personName: string;
    mailboxType?: string;

    favoriteActionSource: ActionSource;
    composeEmailClick?: () => void;
    headerButtonData: HeaderButtonData[];

    personNameDisplayOverride?: string;
    hideFilter?: boolean;
    hideCheckAll?: boolean;
    isInSearch?: boolean;
    headerActionsStylesAsPerUserSettings?: string;
}

@logMountingPerformance('PersonaHeader')
@observer
export default class PersonaHeader extends React.Component<PersonaHeaderProps, {}> {
    private toggleFavoriteDiv: HTMLElement;

    render() {
        const personEmail = this.props.personEmail;
        const titleText = this.props.personNameDisplayOverride || this.props.personName;

        const isPersonFavorite = isPersonaInFavorites(this.props.personaId, personEmail);
        const tableView = getSelectedTableView();
        const isEVHeader = isFeatureEnabled('peo-ev-header');

        const searchHeaderComponent = isFeatureEnabled('sea-lazy2') ? (
            <SearchHeaderFirstRowContentNext className={null} showSpeller={false} />
        ) : (
            <SearchHeaderFirstRowContent className={null} showSpeller={false} />
        );

        return (
            <div>
                {this.shouldShowSearchDiagnostics() && (
                    <div className={styles.searchDiagnostics}>{searchHeaderComponent}</div>
                )}
                <div className={styles.headerContainer}>
                    <div className={styles.imageAndInfoContainer}>
                        {this.wrapInLPCTarget(
                            <PersonaControl
                                emailAddress={this.props.personEmail}
                                name={this.props.personName}
                                size={PersonaSize.regular}
                                mailboxType={this.props.mailboxType}
                            />,
                            false,
                            isEVHeader
                        )}
                        <div className={styles.personInfoContainer}>
                            <div className={styles.firstLine}>
                                {this.wrapInLPCTarget(
                                    <div className={styles.personName}>{titleText}</div>,
                                    true,
                                    isEVHeader
                                )}
                                <div
                                    className={styles.favoriteContainer}
                                    ref={this.toggleFavoriteRendered}>
                                    <ToggleFavoriteButton
                                        isInFavorites={isPersonFavorite}
                                        buttonStyles={styles.favoriteButton}
                                        iconStyles={styles.favoriteIcon}
                                        onClick={this.toggleFavoriteState}
                                        animate={true}
                                    />
                                </div>
                            </div>
                            <div className={styles.personEmail}>{this.props.personEmail}</div>
                        </div>
                    </div>
                    <HeaderActions
                        tableView={tableView}
                        hideCheckAll={this.props.hideCheckAll}
                        hideFilter={this.props.hideFilter}
                        actionButtonData={this.props.headerButtonData}
                        isEVHeader={isEVHeader}
                        headerActionsStylesAsPerUserSettings={
                            this.props.headerActionsStylesAsPerUserSettings
                        }
                    />
                    {this.showPersonaHeaderFirstRunExperience()}
                </div>
            </div>
        );
    }

    componentDidMount(): void {
        this.logHeaderRendered();
    }

    componentDidUpdate(prevProps: PersonaHeaderProps): void {
        if (
            this.props.personaId !== prevProps.personaId ||
            this.props.favoriteActionSource !== prevProps.favoriteActionSource
        ) {
            this.logHeaderRendered();
        }
    }

    private wrapInLPCTarget(
        children: React.ReactNode,
        isHiddenOverflow: boolean,
        isEVHeader: boolean
    ): JSX.Element {
        // Defaults to LPC if locationToOpen is undefined
        const locationToOpen =
            !isEmailAddressDecoratedBrand(this.props.personEmail) && isEVHeader
                ? 'ExpandedView'
                : undefined;

        return (
            <LPCWrapper
                disableHover={true}
                emailAddress={this.props.personEmail}
                name={this.props.personName}
                composeEmailOverride={this.props.composeEmailClick}
                mailBoxType={this.props.mailboxType}
                className={isHiddenOverflow ? styles.hiddenOverflow : undefined}
                clientScenario="PersonaHeader"
                locationToOpen={locationToOpen}>
                {children}
            </LPCWrapper>
        );
    }

    private shouldShowSearchDiagnostics(): boolean {
        let currentSearchQueryId;
        if (isFeatureEnabled('sea-lazy2')) {
            const getSearchStore = lazyGetSearchStore.tryImportForRender();
            currentSearchQueryId =
                getSearchStore && getSearchStore(SearchScenarioIdNext.Mail).currentSearchQueryId;
        } else {
            currentSearchQueryId = getScenarioStore(SearchScenarioId.Mail).currentSearchQueryId;
        }

        return (
            isFeatureEnabled('sea-contextFeedback') &&
            currentSearchQueryId != null &&
            this.props.isInSearch
        );
    }

    private toggleFavoriteRendered = (ref: HTMLElement) => {
        this.toggleFavoriteDiv = ref;

        this.forceUpdate();
    };

    private shouldShowPersonaHeaderFirstRun(): boolean {
        if (!rcStore.recipientCache) {
            return false;
        }

        // Show the persona header first run experience only once
        if (getIsBitEnabled(FavoritesBitFlagsMasks.PersonaHeaderFirstRunSeen)) {
            return false;
        }

        // If the header was displayed from the search
        if (
            this.props.favoriteActionSource !== 'PersonaHeaderFromFolderSearch' &&
            this.props.favoriteActionSource !== 'PersonaHeaderFromPersonaSearch'
        ) {
            return false;
        }

        // // If the user doesn't have any persona favorites
        const favoritePersonasSize = isFeatureEnabled('tri-favorites-roaming')
            ? [...favoritesStore.outlookFavorites.values()].filter(
                  favorite => favorite.type === 'persona'
              ).length
            : favoritesStore.favoritesPersonaNodes.size;

        if (favoritePersonasSize > 0) {
            return false;
        }

        if (!this.props.personEmail) {
            return false;
        }

        const personEmailLowerCase = this.props.personEmail.toLowerCase();

        // If the displayed persona is within the top 100 people returned from findPeople (rcStore.recipientCache is sorted)
        return rcStore.recipientCache
            .slice(0, 100)
            .some(persona =>
                (persona.EmailAddresses || []).some(
                    emailAddress =>
                        (emailAddress.OriginalDisplayName &&
                            emailAddress.OriginalDisplayName.toLowerCase() ===
                                personEmailLowerCase) ||
                        (emailAddress.EmailAddress &&
                            emailAddress.EmailAddress.toLowerCase() === personEmailLowerCase)
                )
            );
    }

    private showPersonaHeaderFirstRunExperience() {
        if (this.toggleFavoriteDiv && this.shouldShowPersonaHeaderFirstRun()) {
            return <PersonaHeaderFirstRun target={this.toggleFavoriteDiv} />;
        }
        return null;
    }

    private toggleFavoriteState = (evt: React.MouseEvent<unknown>) => {
        // Either of the 2 values can be used as a key for this favorite
        const isInProgress =
            isFavoritingInProgress(this.props.personaId) ||
            isFavoritingInProgress(this.props.personEmail);

        if (isInProgress) {
            // Do nothing: favoriting is in progress
            return;
        }

        evt.stopPropagation();
        const isPersonFavorite = isPersonaInFavorites(this.props.personaId, this.props.personEmail);

        let togglingPromise: Promise<{} | void>;

        if (!isPersonFavorite) {
            togglingPromise = addFavoritePersona(
                this.props.personaId,
                this.props.personEmail,
                this.props.personName,
                this.props.favoriteActionSource
            );
        } else {
            const favoriteId = getFavoriteIdForPersona(
                this.props.personaId,
                this.props.personEmail
            );
            togglingPromise = lazyRemoveFavoritePersona.importAndExecute(
                favoriteId,
                this.props.favoriteActionSource
            );
        }

        togglingPromise.catch(() =>
            lazyHandleToggleFavoritePersonaError.importAndExecute(
                isPersonFavorite /*isRemoveFailure*/
            )
        );
    };

    private logHeaderRendered(): void {
        logUsage('PersonaHeaderShown', [this.props.favoriteActionSource]);

        const measure = safePerformanceMeasure('🏃‍ SelectPersonaToPersonaHeaderRender');

        if (measure) {
            const mailListPerceivedPerformanceDataPoint = new PerformanceDatapoint(
                'PerceivedPerformance: PersonaHeaderRender'
            );

            mailListPerceivedPerformanceDataPoint.addCustomProperty(
                'startTime',
                Math.round(measure.startTime)
            );
            mailListPerceivedPerformanceDataPoint.end(Math.round(measure.duration));
        }
    }
}

const LPCWrapper = observerLite(function LPCWrapper(
    props: PersonaCardBehaviorProps & { children: React.ReactNode }
) {
    const PersonaCardBehavior = useLivePersonaCard(props);
    return (
        <PersonaCardBehavior>
            <div title={format(loc(personaHeaderExpandedViewTooltip), props.name)}>
                {props.children}
            </div>
        </PersonaCardBehavior>
    );
});
